// src/components/AutocompleteTagFilter.jsx
import React, { useState, useEffect } from "react";
import axiosBackend from "../api/axiosBackend";

// shadcn
import { Popover, PopoverTrigger, PopoverContent } from "components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "components/ui/command";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";

// icons
import { ChevronDown, X } from "lucide-react";

const AutocompleteTagFilter = ({ selectedTags, setSelectedTags }) => {
  const [tags, setTags] = useState([]);
  const [query, setQuery] = useState("");
  const [filteredTags, setFilteredTags] = useState([]);
  const [open, setOpen] = useState(false);

  // fetch all tags (same logic)
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await axiosBackend.get("/tags/get");
        setTags(response.data);
        setFilteredTags(response.data);
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };
    fetchTags();
  }, []);

  // filter on input change (preserve behavior)
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setFilteredTags(tags);
    } else {
      setFilteredTags(tags.filter((t) => t.name.toLowerCase().includes(q)));
    }
  }, [query, tags]);

  const handleTagSelect = (tag) => {
    if (!selectedTags.some((t) => t._id === tag._id)) {
      setSelectedTags([...selectedTags, tag]);
    }
    setQuery("");
    setOpen(false);
  };

  const handleTagRemove = (tag) => {
    setSelectedTags(selectedTags.filter((t) => t._id !== tag._id));
  };

  return (
    <div className="w-full" dir="rtl">
      {/* Trigger looks like an input so UX matches your original */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setOpen(true)}
              placeholder="מיין לפי תג"
              className="pr-8"
              aria-label="חיפוש תגיות"
            />
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 opacity-60" />
          </div>
        </PopoverTrigger>

        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="end">
          <Command shouldFilter={false}>
            {/* We do our own filtering in state to preserve your logic */}
            <CommandInput
              placeholder="חפש תג…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList className="max-h-64">
              <CommandEmpty>לא נמצאו תגיות</CommandEmpty>
              <CommandGroup>
                {filteredTags.map((tag) => (
                  <CommandItem
                    key={tag._id}
                    value={tag.name}
                    onSelect={() => handleTagSelect(tag)}
                    className="cursor-pointer"
                  >
                    {tag.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected tags */}
      <div className="mt-2 flex flex-wrap gap-2">
        {selectedTags.map((tag) => (
          <Badge
            key={tag._id}
            variant="secondary"
            className="rounded-full px-3 py-1 text-sm bg-primary/10 text-primary border-transparent"
          >
            <span>{tag.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-1 h-5 w-5 p-0"
              onClick={() => handleTagRemove(tag)}
              aria-label={`הסר תג ${tag.name}`}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </Badge>
        ))}
      </div>
    </div>
  );
};

export default AutocompleteTagFilter;
